import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import Select from "@insite/mobius/Select/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MultiSelectDropdown, { Option } from "@tKgpco/Components/MultiSelectDropdown";
import purchaseHistoryReportHandler from "@tKgpco/Handlers/CustomPurchaseHistoryhandler";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import vendorsHandler from "@tKgpco/Store/Data/Vendors/Handlers/VendorsHandler";
import React, { ChangeEvent, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    closePopup: () => void;
    configurationModalIsOpen: boolean;
    reportFormat: string;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: CustomApplicationState) => {
    const session = getSession(state);
    return {
        billTo: getBillToState(state, session.billToId).value,
        vendorsState: state.KGPState.vendorsState,
    };
};

const mapDispatchToProps = {
    vendorsHandler,
    purchaseHistoryReportHandler,
};

export interface CurrentShippingAddressStyles {
    gridContainer?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyProps;
    reportConfigurationModal?: ModalPresentationProps;
    radioButtonStyles?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioOption?: FieldSetPresentationProps<RadioComponentProps>;
    gridItem?: GridItemProps;
    dateGridItem?: GridItemProps;
    datePicker?: DatePickerPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    vendorDropdown?: InjectableCss;
}

export const currentShippingAddressStyles: CurrentShippingAddressStyles = {
    gridContainer: {
        gap: 5,
        css: css`
            border-radius: 4px;
            padding: 10px;
            border: 2px solid #f1f6fb;
        `,
    },
    gridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    dateGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    headingGridItem: { width: 10 },
    headingText: {
        css: css`
            font-size: 1rem;
            font-weight: 600;
        `,
    },
    datePicker: {},

    reportConfigurationModal: { sizeVariant: "medium" },

    centeringWrapper: {
        css: css`
            height: 100px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    vendorDropdown: {
        css: css`
            min-width: 200px !important;
        `,
    },
};

const styles = currentShippingAddressStyles;

const radioOptionsData = [
    {
        value: "manufacturer",
        description: "View by Manufacturer (Includes all Items)",
    },
    {
        value: "item",
        description: "View by Item # (Single Item Only)",
    },
    {
        value: "customer",
        description: "View by Customer Alias (Single Item Only)",
    },
];

const ReportConfigurationModal: React.FunctionComponent<Props> = ({
    closePopup,
    configurationModalIsOpen,
    vendorsHandler,
    vendorsState,
    reportFormat,
    purchaseHistoryReportHandler,
    billTo,
}: Props) => {
    const [searchType, setSearchType] = useState("manufacturer");
    const [searchValue, setSearchValue] = useState("");
    const [fromDate, setFromDate] = useState<Date | undefined>(new Date());
    const [toDate, setToDate] = useState<Date | undefined>(new Date());
    const toasterContext = React.useContext(ToasterContext);
    const [selectedVendors, setSelectedVendors] = useState<Option[]>([]);

    useEffect(() => {
        getVendors();
    }, [configurationModalIsOpen]);

    useEffect(() => {
        setSearchValue("");
    }, [searchType]);

    const getVendors = () => {
        if (configurationModalIsOpen && !vendorsState.vendorsFetched) {
            vendorsHandler({
                onError: message => {
                    toasterContext.addToast({ body: message, messageType: "danger" });
                },
            });
        }
    };

    const validateForm = () => {
        let isValid = false;
        if(searchType === "item" && searchValue) {
            isValid = true;
        } else if(searchType === "customer" && searchValue) {
            isValid = true;
        } else if(searchType === "manufacturer" && selectedVendors.length) {
            isValid = true;
        }
        return !!(searchType && isValid && fromDate && toDate);
    };

    const isValid = validateForm();

    const modalCloseHandler = (e: any) => {
        e.preventDefault();
        clearForm();
        closePopup();
    };

    const radioButtonChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setSearchType(event.target.value);
    };

    const changeDateFormatToString = (date: Date | undefined) => {
        if (!date) {
            return;
        }
        const yyyy = date.getFullYear();
        let mm: string | number = date.getMonth() + 1;
        let dd: string | number = date.getDate();

        if (dd < 10) {
            dd = `0${dd}`;
        }
        if (mm < 10) {
            mm = `0${mm}`;
        }

        return `${yyyy}/${mm}/${dd}`;
    };

    const startDateChangeHandler = ({ selectedDay }: DatePickerState) => {
        setFromDate(selectedDay || new Date());
        if (selectedDay && toDate && selectedDay > toDate) {
            setToDate(undefined);
        }
    };

    const toDateChangeHandler = ({ selectedDay }: DatePickerState) => {
        setToDate(selectedDay);
    };

    const getReport = () => {
        // const isValid = validateForm();
        let isValid = false;
        if(searchType === "item" && searchValue) {
            isValid = true;
        } else if(searchType === "customer" && searchValue) {
            isValid = true;
        } else if(searchType === "manufacturer" && selectedVendors.length) {
            isValid = true;
        }
        if (searchType && isValid && fromDate && toDate) {
            const formData = {
                searchType,
                searchValue: searchType === "manufacturer" ? selectedVendors.map((option) => option.value).join("|"): searchValue,
                fromDate: changeDateFormatToString(fromDate),
                toDate: changeDateFormatToString(toDate),
                outputformat: reportFormat,
            };

            purchaseHistoryReportHandler({
                formData,
                onSuccess: () => {
                    closePopup();
                    clearForm();
                    setSelectedVendors([]);
                    toasterContext.addToast({ body: siteMessage("Report_SendMail"), messageType: "success" });
                },
                onError: message => {
                    toasterContext.addToast({ body: message, messageType: "danger" });
                },
            });
        }
    };

    const clearForm = () => {
        setSearchType("manufacturer");
        setSearchValue("");
        // setFromDate(undefined);
        // setToDate(undefined);
        setSelectedVendors([]);
    };

    const handleDropdownChange = (option: Option) => {
        const isSelected =
            selectedVendors &&
            selectedVendors.length > 0 &&
            selectedVendors.filter(val => val?.value.indexOf(option?.value) !== -1).length > 0;

        let status: Option[] =
        selectedVendors && selectedVendors.length > 0
                ? [...selectedVendors]
                : [];
        if (isSelected) {
            status = selectedVendors!.filter(val => option.value !== val.value);
        } else {
            status.push(option);
        }
        if (status.length > 0) {
            setSelectedVendors(status);
        } else {
            setSelectedVendors([]);
        }
    }

    const options = vendorsState.vendors || [];
    const uniqueOptions = new Map<string, string[]>();
    for (const option of options) {
        const accessorialsOption = uniqueOptions.get(option.name) || [];
        accessorialsOption.push(option.id);
        uniqueOptions.set(option.name, accessorialsOption);
    }

    const dropDownOptions: Option[] = Array.from(uniqueOptions, ([name, values]) => ({ name, values })).map(
        option => {
            let opt = "";
            if(option.name.length === 1 && 
            (option.name.indexOf("'") !== -1 || option.name.indexOf("`") !== -1)) {
                opt = "";
            } else {
                opt = option.name
            }
            return {
            value: option.values.join(","),
            label: opt,
        }},
    ).filter((a) => a.label !== "").sort((a, b) => a.label.localeCompare(b.label));

    const selectedOptions: Option[] =
        selectedVendors && selectedVendors.length > 0
            ? selectedVendors.map(val => ({
                value: val.value,
                label: dropDownOptions.find(opt => opt.value === val.value)?.label || "",
            })
            )
            : [];
    return (
        <Modal
            headline={translate("Configure Your Report")}
            {...styles.reportConfigurationModal}
            isOpen={configurationModalIsOpen}
            handleClose={modalCloseHandler}
        >
            <StyledWrapper>
                <GridContainer>
                    <GridItem {...styles.gridItem}>
                        <Typography as="div">
                            <strong>Customer Number: </strong>
                            <Typography as="span">{billTo?.customerNumber}</Typography>
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.dateGridItem}>
                        <DatePicker
                            {...styles.datePicker}
                            label={translate("Start Date")}
                            selectedDay={fromDate}
                            onDayChange={startDateChangeHandler}
                            dateTimePickerProps={{
                                clearIcon: null,
                                maxDate: new Date(),
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.dateGridItem}>
                        <DatePicker
                            {...styles.datePicker}
                            label={translate("To Date")}
                            selectedDay={toDate}
                            onDayChange={toDateChangeHandler}
                            dateTimePickerProps={{
                                clearIcon: null,
                                minDate: fromDate,
                            }}
                        />
                    </GridItem>
                    <GridItem {...styles.gridItem}>
                        <RadioGroup
                            value={searchType}
                            onChangeHandler={radioButtonChangeHandler}
                            label="Select one of the following options for your Purchase History Report"
                            {...styles.radioButtonStyles}
                        >
                            {radioOptionsData.map(option => {
                                return (
                                    <Radio key={option.value} value={option.value} {...styles.radioOption}>
                                        {translate(option.description)}
                                    </Radio>
                                );
                            })}
                        </RadioGroup>
                    </GridItem>
                    <GridItem {...styles.gridItem}>
                        {searchType === "manufacturer" && vendorsState.isLoading ? (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        ) : searchType === "manufacturer" ? (
                            <>
                                <label>
                                    {`Listed below are the suppliers you made purchases from during [${
                                        fromDate && fromDate.toLocaleDateString()
                                    } - ${toDate && toDate.toLocaleDateString()}]. Select one or
                                    more Suppliers to include on the report. Hold down the "Ctrl" key to select multiple
                                    rows. :`}
                                </label>

                                <MultiSelectDropdown
                                    label=""
                                    handleOptionClick={handleDropdownChange}
                                    options={dropDownOptions}
                                    selectedOptions={selectedOptions}
                                    placeHolder={translate("Please select the vendor")}
                                />
                            </>
                        ) : null}
                        {searchType === "item" && (
                            <StyledWrapper>
                                <TextField
                                    label="Enter an Item # to request the report:"
                                    onChange={e => setSearchValue(e.target.value)}
                                ></TextField>
                            </StyledWrapper>
                        )}
                        {searchType === "customer" && (
                            <StyledWrapper>
                                <TextField
                                    label="Enter a Customer Alias to request the report:"
                                    onChange={e => setSearchValue(e.target.value)}
                                ></TextField>
                            </StyledWrapper>
                        )}
                    </GridItem>
                    <GridItem {...styles.gridItem}>
                        <Button disabled={!isValid} onClick={getReport}>
                            Run Report
                        </Button>
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportConfigurationModal);
